/**
 * Question Filter Helper Functions
 * Handles filtering logic for courses, exams, and subjects
 */

/**
 * Extract unique courses from exam data
 * @param {Array} exams - Array of exam objects
 * @returns {Array} - Array of unique course objects
 */
export const getUniqueCourses = (exams) => {
    if (!exams || !Array.isArray(exams)) return [];
    
    const courseMap = new Map();
    
    exams.forEach((exam) => {
        if (exam?.course_info?._id && exam?.course_info?.name) {
            const courseId = exam.course_info._id;
            if (!courseMap.has(courseId)) {
                courseMap.set(courseId, {
                    _id: exam.course_info._id,
                    name: exam.course_info.name
                });
            }
        }
    });
    
    return Array.from(courseMap.values());
};

/**
 * Extract unique subjects from exam data
 * @param {Array} exams - Array of exam objects
 * @returns {Array} - Array of unique subject objects
 */
export const getUniqueSubjects = (exams) => {
    if (!exams || !Array.isArray(exams)) return [];
    
    const subjectMap = new Map();
    
    exams.forEach((exam) => {
        if (exam?.subject_info?._id && exam?.subject_info?.name) {
            const subjectId = exam.subject_info._id;
            if (!subjectMap.has(subjectId)) {
                subjectMap.set(subjectId, {
                    _id: exam.subject_info._id,
                    name: exam.subject_info.name,
                    courseId: exam.subject_info.courseId
                });
            }
        }
    });
    
    return Array.from(subjectMap.values());
};

/**
 * Extract unique exams from exam data
 * @param {Array} exams - Array of exam objects
 * @returns {Array} - Array of unique exam objects with id, name, and subjects
 */
export const getUniqueExams = (exams) => {
    if (!exams || !Array.isArray(exams)) return [];
    
    const examMap = new Map();
    const examSubjectsMap = new Map(); // Map to store subjects for each exam
    
    exams.forEach((exam) => {
        if (exam?.id && exam?.exam_name) {
            const examId = exam.id;
            
            // Initialize exam if not exists
            if (!examMap.has(examId)) {
                examMap.set(examId, {
                    _id: exam.id,
                    name: exam.exam_name,
                    subjects: []
                });
                examSubjectsMap.set(examId, new Map()); // Map to track unique subjects for this exam
            }
            
            // Add subject to exam if it exists
            if (exam?.subject_info?._id && exam?.subject_info?.name) {
                const subjectMap = examSubjectsMap.get(examId);
                const subjectId = exam.subject_info._id;
                
                if (!subjectMap.has(subjectId)) {
                    subjectMap.set(subjectId, {
                        _id: exam.subject_info._id,
                        name: exam.subject_info.name,
                        courseId: exam.subject_info.courseId
                    });
                }
            }
        }
    });
    
    // Convert subject maps to arrays and assign to exams
    const result = Array.from(examMap.values());
    result.forEach((exam) => {
        const subjectMap = examSubjectsMap.get(exam._id);
        if (subjectMap) {
            exam.subjects = Array.from(subjectMap.values());
        }
    });
    
    return result;
};

/**
 * Filter exams based on selected course, exam, and subject
 * @param {Array} exams - Array of all exam objects
 * @param {string|null} selectedCourseId - Selected course ID
 * @param {string|null} selectedExamId - Selected exam ID
 * @param {string|null} selectedSubjectId - Selected subject ID
 * @returns {Array} - Filtered array of exam objects
 */
export const filterExams = (exams, selectedCourseId, selectedExamId, selectedSubjectId) => {
    if (!exams || !Array.isArray(exams)) return [];
    
    return exams.filter((exam) => {
        // Filter by course
        if (selectedCourseId) {
            if (exam?.course_info?._id !== selectedCourseId) {
                return false;
            }
        }
        
        // Filter by exam
        if (selectedExamId) {
            if (exam?.id !== selectedExamId) {
                return false;
            }
        }
        
        // Filter by subject
        if (selectedSubjectId) {
            if (exam?.subject_info?._id !== selectedSubjectId) {
                return false;
            }
        }
        
        return true;
    });
};

/**
 * Reset filters based on the selected filter type
 * @param {string} filterType - Type of filter selected ('course', 'exam', 'subject')
 * @param {Object} currentFilters - Current filter state
 * @returns {Object} - Updated filter state with appropriate resets
 */
export const resetFilters = (filterType, currentFilters) => {
    const newFilters = { ...currentFilters };
    
    switch (filterType) {
        case 'course':
            // Reset exam and subject when course is selected
            newFilters.selectedExam = null;
            newFilters.selectedSubject = null;
            break;
        case 'exam':
            // Reset course when exam is selected, but keep subject (will be filtered by exam's subjects)
            newFilters.selectedCourse = null;
            // Don't reset subject - it will be filtered to show only subjects belonging to the selected exam
            break;
        case 'subject':
            // Reset course and exam when subject is selected
            newFilters.selectedCourse = null;
            newFilters.selectedExam = null;
            break;
        default:
            break;
    }
    
    return newFilters;
};

